import { useTranslation } from '@jq4race/i18n';
import type { AssignedUserResponse } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface RemoveAssignmentDialogProps {
  user: AssignedUserResponse | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RemoveAssignmentDialog({ user, onConfirm, onCancel }: RemoveAssignmentDialogProps) {
  const { t } = useTranslation('location-assignments');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setSubmitting(false);
      setError(null);
    }
  }, [user]);

  function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      onConfirm();
      onCancel();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('removeDialog.genericError') }));
    } finally {
      setSubmitting(false);
    }
  }

  const email = user?.email ?? '';

  return (
    <Dialog open={!!user} onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('removeDialog.title')}</DialogTitle>
          <DialogDescription>{t('removeDialog.description', { email })}</DialogDescription>
        </DialogHeader>

        <div className="mt-6 rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3">
          <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
            {t('removeDialog.userLabel')}
          </p>
          <p className="mt-1 break-all font-mono text-[13px] text-jq-charcoal">{email}</p>
        </div>

        {error && (
          <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={confirm}
            disabled={submitting}
          >
            {submitting ? t('removeDialog.removing') : t('removeDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
